import { useEffect, useLayoutEffect, useRef } from 'react';
import { useStore } from '@tanstack/react-form';
import { useProposalDetails } from '@/features/quotes/hooks/homeowner/use-proposal-details.hook';
import { toaster } from '@/shared/ui/toaster.component';
import {
  capitalGlobalStep1PrefillFingerprint,
  capitalGlobalStep1ValuesFingerprint,
  mapProposalDetailsToCapitalGlobalStep1,
} from '@/features/quotes/components/capital-global/steps/utils/map-proposal-details-to-capital-global-step1.util';
import type { SubStipulantEntry } from '@/features/quotes/components/capital-global/types';

type PrefillHookParams = {
  resolvedPolicyId: string;
  hasPolicyId: boolean;
  formStore: any;
  setFieldValue: (...args: any[]) => void;
};

export function useCapitalGlobalStep1Prefill({
  resolvedPolicyId,
  hasPolicyId,
  formStore,
  setFieldValue,
}: PrefillHookParams) {
  const {
    data: proposalFromApi,
    dataUpdatedAt,
    isLoading: isLoadingProposal,
    isError,
    error: proposalDetailsError,
  } = useProposalDetails(hasPolicyId ? resolvedPolicyId : undefined);

  const lastAppliedServerFingerprintRef = useRef('');
  const userEditedAfterServerHydrationRef = useRef(false);
  const isApplyingServerPrefillRef = useRef(false);
  const prevResolvedPolicyIdRef = useRef<string | undefined>(undefined);
  const proposalErrorToastPolicyIdRef = useRef<string | null>(null);

  const step1FormFingerprint = useStore(formStore, state => {
    const values = (state as any).values as {
      insuranceType: 'new' | 'renewal';
      congenere: string;
      policyNumber: string;
      useIndividualDocument: boolean;
      documentNumber: string;
      cpf: string;
      name: string;
      businessSegment: string;
      businessCnae: string;
      averagePartnerAge?: number;
      averageEmployeeAge?: number;
      partnerCount?: number;
      employeeCount?: number;
      subStipulants: SubStipulantEntry[];
    };
    return capitalGlobalStep1ValuesFingerprint({
      insuranceType: values.insuranceType,
      congenere: String(values.congenere ?? ''),
      policyNumber: String(values.policyNumber ?? ''),
      useIndividualDocument: Boolean(values.useIndividualDocument),
      documentNumber: String(values.documentNumber ?? ''),
      cpf: String(values.cpf ?? ''),
      name: String(values.name ?? ''),
      businessSegment: String(values.businessSegment ?? ''),
      businessCnae: String(values.businessCnae ?? ''),
      averagePartnerAge: values.averagePartnerAge,
      averageEmployeeAge: values.averageEmployeeAge,
      partnerCount: values.partnerCount,
      employeeCount: values.employeeCount,
      subStipulants: (values.subStipulants ?? []) as SubStipulantEntry[],
    });
  });

  useLayoutEffect(() => {
    if (prevResolvedPolicyIdRef.current === resolvedPolicyId) return;
    prevResolvedPolicyIdRef.current = resolvedPolicyId;
    lastAppliedServerFingerprintRef.current = '';
    userEditedAfterServerHydrationRef.current = false;
  }, [resolvedPolicyId]);

  useLayoutEffect(() => {
    if (!hasPolicyId || !proposalFromApi) return;

    const nextFp = capitalGlobalStep1PrefillFingerprint(proposalFromApi);
    if (nextFp === lastAppliedServerFingerprintRef.current) return;
    if (
      userEditedAfterServerHydrationRef.current &&
      lastAppliedServerFingerprintRef.current !== ''
    ) {
      return;
    }

    const prefill = mapProposalDetailsToCapitalGlobalStep1(proposalFromApi);
    isApplyingServerPrefillRef.current = true;
    try {
      setFieldValue('insuranceType', prefill.insuranceType);
      setFieldValue('congenere', prefill.congenere);
      setFieldValue('policyNumber', prefill.policyNumber);
      setFieldValue('useIndividualDocument', prefill.useIndividualDocument);
      setFieldValue('documentNumber', prefill.documentNumber);
      setFieldValue('cpf', prefill.cpf);
      setFieldValue('name', prefill.name);
      setFieldValue('businessSegment', prefill.businessSegment);
      setFieldValue('businessCnae', prefill.businessCnae);
      setFieldValue('averagePartnerAge', prefill.averagePartnerAge);
      setFieldValue('averageEmployeeAge', prefill.averageEmployeeAge);
      setFieldValue('partnerCount', prefill.partnerCount);
      setFieldValue('employeeCount', prefill.employeeCount);
      setFieldValue('subStipulants', prefill.subStipulants);
    } finally {
      isApplyingServerPrefillRef.current = false;
    }
    lastAppliedServerFingerprintRef.current = nextFp;
  }, [hasPolicyId, proposalFromApi, dataUpdatedAt, setFieldValue]);

  useEffect(() => {
    if (isApplyingServerPrefillRef.current) return;
    if (lastAppliedServerFingerprintRef.current === '') return;
    if (step1FormFingerprint === lastAppliedServerFingerprintRef.current)
      return;
    userEditedAfterServerHydrationRef.current = true;
  }, [step1FormFingerprint]);

  useEffect(() => {
    if (!hasPolicyId || !isError) {
      proposalErrorToastPolicyIdRef.current = null;
      return;
    }
    if (proposalErrorToastPolicyIdRef.current === resolvedPolicyId) return;
    proposalErrorToastPolicyIdRef.current = resolvedPolicyId;
    toaster.create({
      type: 'error',
      title: 'Não foi possível carregar a cotação',
      description:
        proposalDetailsError instanceof Error
          ? proposalDetailsError.message
          : 'Erro ao buscar detalhes da proposta',
    });
  }, [hasPolicyId, isError, resolvedPolicyId, proposalDetailsError]);

  return {
    showProposalLoading: hasPolicyId && isLoadingProposal && !proposalFromApi,
  };
}
